import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { usePendingConsents, useRecordMultipleConsents, ConsentDocument } from '@/hooks/useConsent';
import { ConsentDialog } from '@/components/consent/ConsentDialog';
import { Loader2 } from 'lucide-react';

interface ConsentGuardProps {
  userId: string;
  children: ReactNode;
}

/**
 * ConsentGuard - HIPAA Compliance Component
 * 
 * Wraps protected content and ensures users have accepted all required consent documents
 * before accessing the application. This is a critical HIPAA compliance requirement.
 * 
 * Required consent types:
 * - hipaa_authorization: HIPAA Authorization for PHI access
 * - privacy_policy: Privacy Policy acceptance
 * - terms_of_service: Terms of Service acceptance
 */
export function ConsentGuard({ userId, children }: ConsentGuardProps) {
  const { t } = useTranslation('common');
  const { data: pendingConsents, isLoading, isError, error, refetch } = usePendingConsents(userId);
  const recordConsentsMutation = useRecordMultipleConsents();

  // Handle error state - show children anyway with a warning (fail open for UX, log for monitoring)
  if (isError) {
    console.error('Failed to check consent status:', error);
    // Fall through to render children - consent check failed silently
    return <>{children}</>;
  }

  // Show loading state while checking consents
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">
            {t('consent.checking', 'Checking consent requirements...')}
          </p>
        </div>
      </div>
    );
  }

  // Check if there are pending consents
  const hasPendingConsents = pendingConsents && pendingConsents.length > 0;

  // Handle consent completion
  const _handleConsentComplete = async (consents: Array<{ document: ConsentDocument; consented: boolean }>) => {
    try {
      await recordConsentsMutation.mutateAsync({
        userId,
        consents,
      });
      // Refetch to verify all consents are now recorded
      await refetch();
    } catch (error) {
      console.error('Failed to record consents:', error);
    }
  };

  // If there are pending consents, show the consent dialog
  if (hasPendingConsents) {
    return (
      <ConsentDialog
        open={true}
        onOpenChange={() => {}} // Prevent closing without accepting
        userId={userId}
        onComplete={() => {
          refetch();
        }}
        requiredTypes={pendingConsents.map(doc => doc.consent_type as 'hipaa_authorization' | 'privacy_policy' | 'terms_of_service' | 'data_processing')}
      />
    );
  }

  // All consents are given, render children
  return <>{children}</>;
}
